import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { geocodingAPI } from '../services/api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const containerStyle = {
  width: '100%',
  height: '500px',
};

const defaultCenter = {
  lat: -31.4135,
  lng: -64.1811, // Córdoba, Argentina
};

// Recentra el mapa cuando cambia la posición seleccionada desde afuera (ej. al abrir el diálogo)
function RecenterMap({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);

  return null;
}

function MapClickHandler({ onMapClick, disabled }) {
  useMapEvents({
    click: (e) => {
      if (disabled) return;
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });

  return null;
}

const MapLocationPicker = ({ open, onClose, onSelectLocation, initialLat, initialLng, title, readOnly = false }) => {
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);

  // Initialize position when dialog opens
  useEffect(() => {
    if (open && initialLat && initialLng) {
      const lat = parseFloat(initialLat);
      const lng = parseFloat(initialLng);
      if (!isNaN(lat) && !isNaN(lng)) {
        setSelectedPosition({ lat, lng });
        reverseGeocode(lat, lng);
      }
    } else if (open && !initialLat && !initialLng) {
      // If no initial coordinates, use default center
      setSelectedPosition(defaultCenter);
      reverseGeocode(defaultCenter.lat, defaultCenter.lng);
    }
  }, [open, initialLat, initialLng]);

  const reverseGeocode = async (lat, lng) => {
    setLoading(true);
    try {
      const response = await geocodingAPI.reverse(lat, lng);
      setAddress(response.data?.data?.address || 'Dirección no disponible');
    } catch (error) {
      console.error('Error en geocodificación inversa:', error);
      setAddress('Error al obtener dirección');
    } finally {
      setLoading(false);
    }
  };

  const handleMapClick = useCallback((lat, lng) => {
    setSelectedPosition({ lat, lng });
    reverseGeocode(lat, lng);
  }, []);

  const handleMarkerDragEnd = useCallback((event) => {
    const { lat, lng } = event.target.getLatLng();
    setSelectedPosition({ lat, lng });
    reverseGeocode(lat, lng);
  }, []);

  const handleConfirm = () => {
    if (selectedPosition) {
      onSelectLocation({
        lat: selectedPosition.lat,
        lng: selectedPosition.lng,
        address: address,
      });
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedPosition(null);
    setAddress('');
    onClose();
  };

  const center = selectedPosition || defaultCenter;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>{title || 'Seleccionar Ubicación'}</DialogTitle>
      <DialogContent>
        {!readOnly && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Haz clic en el mapa o arrastra el marcador para seleccionar una ubicación
            </Typography>
          </Box>
        )}

        <Box sx={{ ...containerStyle, borderRadius: 1, overflow: 'hidden' }}>
          <MapContainer
            center={center}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapClickHandler onMapClick={handleMapClick} disabled={readOnly} />
            <RecenterMap position={selectedPosition} />

            {selectedPosition && (
              <Marker
                position={selectedPosition}
                draggable={!readOnly}
                eventHandlers={readOnly ? undefined : { dragend: handleMarkerDragEnd }}
              />
            )}
          </MapContainer>
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Coordenadas"
            value={
              selectedPosition
                ? `${selectedPosition.lat.toFixed(6)}, ${selectedPosition.lng.toFixed(6)}`
                : ''
            }
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            label="Dirección"
            value={loading ? 'Obteniendo dirección...' : address}
            InputProps={{
              readOnly: true,
            }}
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {readOnly ? (
          <Button onClick={handleCancel}>Cerrar</Button>
        ) : (
          <>
            <Button onClick={handleCancel} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={!selectedPosition}
            >
              Confirmar Ubicación
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MapLocationPicker;
